import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

/**
 * `<product-inventory-indicator>`
 *
 * A small status indicator (animated dot + message) shown above the
 * Add to Cart button, reflecting the *currently selected* variant's
 * live inventory state.
 *
 * Ported from the Horizon storefront's `ThemeEvents.variantUpdate` bus to
 * this theme's newer `StandardEvents.productSelect` promise-based event
 * (see assets/product-inventory.js for the sibling component this mirrors).
 * Rather than re-deriving the low/medium/high threshold text from a raw
 * variant object client-side, this listens for the same event, waits on
 * `event.promise` for the freshly rendered section HTML, and morphs in the
 * already-computed `<product-inventory-indicator>` markup from the server
 * response - keeping the quantity-threshold copy logic in one place
 * (Liquid) instead of duplicating it in JS.
 */
class ProductInventoryIndicator extends Component {
  /** @type {HTMLElement | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          // A combined-listing swap loaded a different product - track its id
          // so future events for this product resolve correctly.
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          // This update belongs to a different product form on the page
          // (e.g. another quick-add modal) - ignore it.
          return;
        }

        const newIndicator = html.querySelector('product-inventory-indicator');
        if (!newIndicator) return;

        this.#applyMorph(newIndicator);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[product-inventory-indicator] Event promise rejected:', error);
        }
      });
  };

  /**
   * @param {Element} newIndicator
   */
  #applyMorph(newIndicator) {
    const wasHidden = this.hidden;
    const willBeHidden = newIndicator.hasAttribute('hidden');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const textElement = this.querySelector('.product-inventory-indicator__text');
    const newTextElement = newIndicator.querySelector('.product-inventory-indicator__text');
    const unchanged =
      !willBeHidden &&
      !wasHidden &&
      textElement &&
      newTextElement &&
      textElement.textContent === newTextElement.textContent &&
      this.className === newIndicator.className;

    if (unchanged) return;

    if (wasHidden || willBeHidden || reducedMotion) {
      // Nothing was visible before (or won't be after), or the visitor
      // prefers reduced motion - swap immediately, no crossfade needed.
      morph(this, newIndicator, { childrenOnly: true });
      if (newIndicator.hasAttribute('hidden')) {
        this.hidden = true;
      } else {
        this.hidden = false;
      }
      this.className = newIndicator.className;
      return;
    }

    // Smooth fade: fade the current message out, morph while invisible,
    // then fade the new message back in. The element itself is never
    // removed or recreated - only its contents/classes change.
    this.classList.add('product-inventory-indicator--transitioning');
    window.clearTimeout(this.#fadeTimeout);
    this.#fadeTimeout = window.setTimeout(() => {
      morph(this, newIndicator, { childrenOnly: true });
      this.className = newIndicator.className;
      this.hidden = newIndicator.hasAttribute('hidden');
      this.classList.remove('product-inventory-indicator--transitioning');
    }, 150);
  }

  /** @type {number | undefined} */
  #fadeTimeout;
}

if (!customElements.get('product-inventory-indicator')) {
  customElements.define('product-inventory-indicator', ProductInventoryIndicator);
}
